import React from 'react';

import styles from './Toolbox.module.css';

interface ToolboxItemProps {
  type: string;
  label: string;
  icon: string;
}

const ITEMS: ToolboxItemProps[] = [
  { type: 'NO_CONTACT', label: 'NO Contact', icon: '──[ ]──' },
  { type: 'NC_CONTACT', label: 'NC Contact', icon: '──[/]──' },
  { type: 'P_CONTACT', label: 'P Contact (Rising Edge)', icon: '──[P]──' },
  { type: 'N_CONTACT', label: 'N Contact (Falling Edge)', icon: '──[N]──' },
  { type: 'NOT_CONTACT', label: 'NOT (Invert RLO)', icon: '──[NOT]──' },
  { type: 'COIL', label: 'Coil', icon: '──( )──' },
  { type: 'INVERT_COIL', label: 'Inverted Coil', icon: '──(/)──' },
  { type: 'SET_COIL', label: 'Set Coil', icon: '──(S)──' },
  { type: 'RESET_COIL', label: 'Reset Coil', icon: '──(R)──' },
  { type: 'SR', label: 'SR Flip-Flop (Set Dominant)', icon: '[ SR ]' },
  { type: 'RS', label: 'RS Flip-Flop (Reset Dominant)', icon: '[ RS ]' },
  { type: 'BRANCH', label: 'Parallel Branch', icon: '  │ ├──' },
  { type: 'H_LINE', label: 'Horizontal Line', icon: '───────' },
  { type: 'V_LINE', label: 'Vertical Line', icon: '   │   ' },
  { type: 'TON', label: 'TON Timer', icon: '[ TON ]' },
  { type: 'TOF', label: 'TOF Timer', icon: '[ TOF ]' },
  { type: 'TP', label: 'TP Timer', icon: '[ TP ]' },
  { type: 'TONR', label: 'TONR Retentive Timer', icon: '[TONR]' },
  { type: 'CTU', label: 'CTU Counter', icon: '[ CTU ]' },
  { type: 'CTD', label: 'CTD Counter', icon: '[ CTD ]' },
  { type: 'CTUD', label: 'CTUD Counter', icon: '[CTUD]' },
  { type: 'CMP_EQ_I', label: '==I (Equal)', icon: '[ ==I ]' },
  { type: 'CMP_NE_I', label: '<>I (Not Eq)', icon: '[ <>I ]' },
  { type: 'CMP_GT_I', label: '>I (Greater)', icon: '[ >I ]' },
  { type: 'CMP_LT_I', label: '<I (Less)', icon: '[ <I ]' },
  { type: 'CMP_GE_I', label: '>=I (GE)', icon: '[ >=I ]' },
  { type: 'CMP_LE_I', label: '<=I (LE)', icon: '[ <=I ]' },
  { type: 'CMP_EQ_R', label: '==R (Float Eq)', icon: '[ ==R ]' },
  { type: 'CMP_NE_R', label: '<>R (Float Not Eq)', icon: '[ <>R ]' },
  { type: 'CMP_GT_R', label: '>R (Float Greater)', icon: '[ >R ]' },
  { type: 'CMP_LT_R', label: '<R (Float Less)', icon: '[ <R ]' },
  { type: 'CMP_GE_R', label: '>=R (Float GE)', icon: '[ >=R ]' },
  { type: 'CMP_LE_R', label: '<=R (Float LE)', icon: '[ <=R ]' },
  { type: 'ADD_I', label: 'ADD_I', icon: '[ ADD ]' },
  { type: 'SUB_I', label: 'SUB_I', icon: '[ SUB ]' },
  { type: 'MUL_I', label: 'MUL_I', icon: '[ MUL ]' },
  { type: 'DIV_I', label: 'DIV_I', icon: '[ DIV ]' },
  { type: 'ADD_R', label: 'ADD_R (Float)', icon: '[ADD_R]' },
  { type: 'SUB_R', label: 'SUB_R (Float)', icon: '[SUB_R]' },
  { type: 'MUL_R', label: 'MUL_R (Float)', icon: '[MUL_R]' },
  { type: 'DIV_R', label: 'DIV_R (Float)', icon: '[DIV_R]' },
  { type: 'MOV_W', label: 'MOV_W (Word)', icon: '[MOV_W]' },
  { type: 'MOV_R', label: 'MOV_R (Real)', icon: '[MOV_R]' },
  { type: 'SCALE', label: 'SCALE (Linear Scale)', icon: '[SCALE]' },
  { type: 'IN_RANGE', label: 'IN_RANGE (Range Check)', icon: '[RANGE]' },
  { type: 'JMP', label: 'JMP (Jump)', icon: '──(JMP)──' },
  { type: 'LBL', label: 'LBL (Label)', icon: '──(LBL)──' }
];

import { useEffect } from 'react';
import { useLadderEditorStore } from './store/ladderEditorStore';
import { useSimulationStore } from '../../../store/simulationStore';

export function Toolbox() {
  const { activeTool, setActiveTool } = useLadderEditorStore();
  const isSimulationRunning = useSimulationStore(s => s.snapshot.state !== 'STOPPED');

  useEffect(() => {
    if (isSimulationRunning && activeTool) {
      setActiveTool(null);
    }
  }, [isSimulationRunning, activeTool, setActiveTool]);

  const handleDragStart = (e: React.DragEvent, type: string) => {
    if (isSimulationRunning) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('application/ladder-element', type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleClick = (type: string) => {
    if (isSimulationRunning) return;
    setActiveTool(activeTool === type ? null : type);
  };

  return (
    <div 
      className={styles.toolbox}
      style={isSimulationRunning ? { opacity: 0.6, pointerEvents: 'none', userSelect: 'none' } : undefined}
    >
      <h3 className={styles.title}>Toolbox</h3>
      <div className={styles.list}>
        {ITEMS.map(item => {
          const isActive = activeTool === item.type;
          const tooltip = item.type === 'H_LINE'
            ? `${item.label} (Seri çizmek için tıklayıp sürükleyin — Kısayol: H veya -)`
            : item.label;

          return (
            <div
              key={item.type}
              className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
              draggable={!isSimulationRunning}
              onDragStart={(e) => handleDragStart(e, item.type)}
              onClick={() => handleClick(item.type)}
              title={tooltip}
            >
              <div className={styles.icon}>{item.icon}</div>
              <div className={styles.label}>{item.label}</div>
              {isActive && <div className={styles.activeBadge}>Aktif</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
